/**
 * Copyright Blue Billywig 2018+
 */

import { tassign } from 'tassign';

import { TimelineDataActions } from './timeline-data-actions';
import { ITimelineData, IDataWidget, IStateSet, IState, IInOutPoint, IDataTrackAreaItem, IAnimationData, IMessage } from './timeline-data.model';
import { ITrackAreaItem } from '../interfaces/ITrackAreaItem';
import { InteractivityWidgetAnimationService } from '../services/interactivity-widget-animation.service';

export interface ITimelineDataState {	// Timeline State interface for redux
	data: ITimelineData;
	fromCommand: string;
	type: string;
}

export const TIMELINE_DATA_INITIAL_STATE: ITimelineDataState = { // initial state with empty Timeline Data
	data: {
		subtype: 'versioned',
		modelVersion: '2',
		id: null,
		status: 'published',
		parentEntityType: null,
		parentEntityId: null,
		createddate: '',
		updateddate: '',
		interactions: [],
		groupTags: [],
		widgets: [],
		order: [],
		forms: [{
			name: '',
			method: 'POST',
			action: '',
			email: '',
			formEmailTemplate: '',
			formEmailFrom: '',
			onSuccess: [],
			onFailure: [],
			onAllWidgetsValidated: [],
			id: '1'
		}],
		options: { exitArea: false }
	},
	fromCommand: 'Initial timeline data state',
	type: 'CREATE_NEW'
};

export function timelineDataReducer(state: ITimelineDataState = TIMELINE_DATA_INITIAL_STATE, action: any): ITimelineDataState {
	let newState: ITimelineDataState;
	let newData: any;
	let widget: IDataWidget;
	let widgetIndex: number;
	let newOrder: IDataTrackAreaItem[] = [];

	state = removeFaultyWidgetsAndTracks(state);


	switch (action.type) {
		case '__INIT__':
			return TIMELINE_DATA_INITIAL_STATE;

		case TimelineDataActions.NEW:
			if (action.mergeData) {
				const mergedData = { ...JSON.parse(JSON.stringify(TIMELINE_DATA_INITIAL_STATE)).data, ...action.mergeData };
				newState = tassign(TIMELINE_DATA_INITIAL_STATE, { data: mergedData, fromCommand: 'New Timeline' });
			} else {
				newState = tassign(TIMELINE_DATA_INITIAL_STATE, { fromCommand: 'New Timeline' });
			}
			break;

		case TimelineDataActions.LOADED:
			if (action.loadData && action.loadData.data) {
				newState = tassign(state, { data: action.loadData.data, fromCommand: action.description });
			} else if (action.data !== null) {
				newState = tassign(state, { data: action.data, fromCommand: action.description });
			}
			newState = removeFaultyWidgetsAndTracks(newState);
			if (!newState.fromCommand) {
				newState.fromCommand = 'Loaded';
			}
			break;

		case TimelineDataActions.UPDATE_DATA:
			if (action.data !== null) {
				newState = tassign(state, { data: action.data, fromCommand: action.description });
			}
			newState = removeFaultyWidgetsAndTracks(newState);

			if (!newState.fromCommand) {
				// try to always have a fromCommand!
				newState.fromCommand = 'Updated timeline data';
			}
			break;

		case TimelineDataActions.ADD_MESSAGE:
			if (action.messageName) {
				const newMessage: IMessage = {
					name: action.messageName,
					procedures: [],
				};

				newData = {
					...state.data, ...{
						messages: [...(state.data.messages || []), newMessage]
					}
				};
				newState = tassign(newState, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.CHANGE_MESSAGE:
			if (action.messageName) {
				let messageIndex = -1;
				const newMessage: IMessage = state.data.messages.find((message: IMessage, index) => {
					messageIndex = index;
					return message.name === action.messageName;
				});

				newData = {
					...state.data, ...{
						messages: [
							...state.data.messages.slice(0, messageIndex),
							tassign(newMessage, {
								name: action.newMessageName || action.messageName,
								procedures: action.messageProcedures ? JSON.parse(JSON.stringify(action.messageProcedures)) : newMessage.procedures
							}),
							...state.data.messages.slice(messageIndex + 1)
						]
					}
				};
				newState = tassign(newState, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.REMOVE_MESSAGE:
			if (action.messageName) {
				const messageIndex = state.data.messages.findIndex((message: IMessage, index) => message.name === action.messageName);

				const newMessages = [...state.data.messages];
				newMessages.splice(messageIndex, 1);

				newData = {
					...state.data, ...{
						messages: newMessages
					}
				};
				newState = tassign(newState, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.ADD_WIDGET:
			if (action.widget != null && action.ioPoint) {
				const newWidgets: IDataWidget[] = new Array<IDataWidget>();
				newWidgets.push(action.widget);
				newData = Object.assign({}, state.data, { widgets: state.data.widgets.concat(newWidgets) });
				newState = tassign(state, { data: newData });

				// create new Track for new widget
				const newTrack: IDataTrackAreaItem = {
					id: String(Math.floor(Math.random() * 9898946546) + 1),
					type: 'track',
					widgetid: action.widget.id,
					targetIndex: action.targetIndex,
					inoutpoints: [{
						id: String(Math.floor(Math.random() * 9898946546) + 1),
						inpoint: action.ioPoint.inPoint,
						outpoint: action.ioPoint.outPoint
					}]
				};

				// add new track using reducer
				newState = timelineDataReducer(newState, {
					type: TimelineDataActions.ADD_TRACK,
					intoTrackGroupId: action.intoTrackGroupId,
					track: newTrack
				});

				newState = tassign(newState, { fromCommand: action.description });
			}
			break;

		case TimelineDataActions.IMPORT_TIMELINE_DATA:
			if (action.tlData) {
				newData = { ...state.data };
				if (action.tlData.forms) {
					if (!newData.forms || newData.forms.length === 0) {
						newData.forms = action.tlData.forms;
					}
					// TODO: what should happen when we import a form but already have one?
					// old interface would overrule existing form..
					// newData.forms = [...(newData.forms || []), ...action.tlData.forms];
				}
				if (action.tlData.groupTags) {
					newData.groupTags = [...(newData.groupTags || []), ...action.tlData.groupTags];
				}
				if (action.tlData.messages) {
					newData.messages = [...(newData.messages || []), ...action.tlData.messages];
				}
				if (action.tlData.order) {
					newData.order = [...newData.order, ...action.tlData.order];
				}
				if (action.tlData.widgets) {
					newData.widgets = [...newData.widgets, ...action.tlData.widgets];
				}
				newState = tassign(state, { data: newData, fromCommand: action.description });
			}
			break;

		case TimelineDataActions.SET_DESIGN_DIMENSIONS:
			if (action.designWidth && action.designHeight) {
				newData = { ...state.data };
				newData.options = newData.options ? { ...newData.options } : { ...TIMELINE_DATA_INITIAL_STATE.data.options };
				newData.options.designWidth = Number(action.designWidth);
				newData.options.designHeight = Number(action.designHeight);
				newState = tassign(state, { data: newData, fromCommand: action.description });
			}
			break;

		case TimelineDataActions.SET_ENDSCREEN_AREA:
			if (action.useEndscreen !== undefined) {
				newData = { ...state.data };
				newData.options = newData.options ? { ...newData.options } : { ...TIMELINE_DATA_INITIAL_STATE.data.options };
				newData.options.exitArea = !!action.useEndscreen;

				newState = tassign(state, { data: newData, fromCommand: action.description });
			}
			break;

		case TimelineDataActions.DUPLICATE_WIDGETS:
			if (action.duplicateActions && action.duplicateActions.length) {
				newData = { ...state.data };
				newState = tassign(state, { data: newData });
				action.duplicateActions.forEach((duplicateAction: any) => {
					newState = timelineDataReducer(newState, duplicateAction);
				});
			}
			newState = tassign(newState, { fromCommand: action.description });
			break;

		case TimelineDataActions.DUPLICATE_WIDGET:
			const fromWidget = state.data.widgets.find(w => w.id === action.fromWidgetId) || action.widgetData;
			if (fromWidget) {
				let widgetDuplicate: IDataWidget = JSON.parse(JSON.stringify(fromWidget));
				let fromTrackObj = findTrackByWidgetId(fromWidget.id, state.data.order);


				if ((!fromTrackObj || !fromTrackObj.track) && action.trackData) {
					fromTrackObj = {
						track: action.trackData,
						trackIndex: 0
					};
				}
				// console.warn('widgetDuplicate', widgetDuplicate, 'fromTrack', fromTrackObj)
				widgetDuplicate = { ...widgetDuplicate, ...(action.widgetOverrides || {}) };
				if (action.toPosition) {
					// TODO (?) update all statesets with delta positions?
					widgetDuplicate.statesets[0].states[0].config.xpos = action.toPosition.xpos;
					widgetDuplicate.statesets[0].states[0].config.ypos = action.toPosition.ypos;
				}
				if (widgetDuplicate != null && fromTrackObj && fromTrackObj.track && fromTrackObj.track.inoutpoints) {
					const newWidgets: IDataWidget[] = [];
					newWidgets.push(widgetDuplicate);
					newData = Object.assign({}, state.data, { widgets: state.data.widgets.concat(newWidgets) });
					newState = tassign(state, { data: newData });

					// create new Track for new widget
					const newTrack: IDataTrackAreaItem = {
						id: String(Math.floor(Math.random() * 9898946546) + 1),
						type: 'track',
						widgetid: widgetDuplicate.id,
						targetIndex: fromTrackObj.trackIndex ? (fromTrackObj.trackIndex + (fromTrackObj.groupId ? 0 : 1)) : 0, // not sure why..
						inoutpoints: [{
							id: String(Math.floor(Math.random() * 9898946546) + 1),
							inpoint: fromTrackObj.track.inoutpoints[0].inpoint,
							outpoint: fromTrackObj.track.inoutpoints[0].outpoint
						}]
					};

					// add new track using reducer
					newState = timelineDataReducer(newState, {
						type: TimelineDataActions.ADD_TRACK,
						intoTrackGroupId: fromTrackObj.groupId,
						track: newTrack
					});

					newState = tassign(newState, { fromCommand: action.description });
				}
			}
			break;

		case TimelineDataActions.CHANGE_GROUP_TAG_LABEL:
			// untested code
			const newTags = [...state.data.groupTags];
			for (let i = 0, l = newTags.length; i < l; i++) {
				if (newTags[i] && newTags[i].id === action.newTag.id) {
					newTags[i] = {
						id: action.newTag.id,
						label: action.newTag.label
					};
				}
			}

			newData = { ...state.data };
			newData.groupTags = newTags;
			newState = tassign(state, { data: newData, fromCommand: action.description });
			break;

		case TimelineDataActions.CHANGE_FORM_FIELD:

			newData = { ...state.data };
			newData.forms = newData.forms ? [...newData.forms] : [];
			newData.forms[0] = newData.forms[0] ? { ...newData.forms[0] } : {
				name: '',
				method: 'POST',
				action: '',
				email: '',
				formEmailTemplate: '',
				formEmailFrom: '',
				onSuccess: [],
				onFailure: [],
				onAllWidgetsValidated: [],
				id: '1'
			};
			const form = newData.forms[0];
			if (action.fieldName && action.value !== undefined) {
				form[action.fieldName] = action.value;
			}
			newState = tassign(state, { data: newData, fromCommand: action.description });
			break;

		case TimelineDataActions.ADD_REMOVE_TAG_ON_WIDGETS:
			let newTag = action.newTag;
			if (action.widgets && action.widgets.length) {
				newState = state;
				action.widgets.forEach((w: IDataWidget) => {
					const tags: any = [...w.grouping];
					const tagIndex = tags.indexOf(action.tag.id);
					if (action.remove) {
						if (tagIndex !== -1) {
							tags.splice(tagIndex, 1);
						}
					} else {
						if (tagIndex === -1) {
							tags.push(action.tag.id);
						}
					}
					if (w.grouping.length !== tags.length) {
						newState = timelineDataReducer(newState, {
							type: TimelineDataActions.CHANGE_WIDGET,
							id: w.id,
							tags,
							newTag
						});
						newTag = null; // first widget change will add new tag
					}
				});
			}
			break;

		case TimelineDataActions.CHANGE_WIDGET:
			widget = state.data.widgets.find(w => w.id === action.id);
			widgetIndex = state.data.widgets.indexOf(widget);

			const newWidget = tassign(widget);
			if (action.label !== undefined) {
				newWidget.label = action.label;
			}
			if (action.type !== undefined && action.typeId !== undefined) {
				newWidget.widgetdefinitiontype = action.type;
				newWidget.widgetdefinitionid = action.typeId;
			}

			if (action.tags) {
				newWidget.grouping = [...action.tags];
			}

			newData = tassign(state.data, {
				widgets: [
					...state.data.widgets.slice(0, widgetIndex),
					newWidget,
					...state.data.widgets.slice(widgetIndex + 1),
				]
			});
			if (action.newTag) {
				const newGroupTags = newData.groupTags ? [...newData.groupTags] : [];
				newGroupTags.push(action.newTag);
				newData.groupTags = newGroupTags;
			}
			newState = tassign(state, { data: newData, fromCommand: action.description });
			break;

		case TimelineDataActions.CHANGE_WIDGET_CONFIG:
			if (action.id != null && action.stateSet != null && action.state != null && action.config != null) {
				const currentStateSet = action.stateSet;
				const currentState = action.state;

				widget = state.data.widgets.find(w => w.id === action.id);
				if (widget) { // found
					const widgetStateSet: IStateSet = widget.statesets.find(set => set.name === currentStateSet);
					const widgetState: IState = widgetStateSet.states.find(s => s.name === currentState);

					if (widget && widgetStateSet && widgetState) {
						const widgetStateIndex = widgetStateSet.states.indexOf(widgetState);
						const widgetStateSetIndex = widget.statesets.indexOf(widgetStateSet);

						const newConfig: any = Object.assign({}, widgetState.config, action.config);
						const newWidgetState: IState = Object.assign({}, widgetState, { config: newConfig });

						const newWidgetStateSet: IStateSet = tassign(widgetStateSet, {
							states: [
								...widgetStateSet.states.slice(0, widgetStateIndex),
								newWidgetState,
								...widgetStateSet.states.slice(widgetStateIndex + 1)]
						});

						const newWidgetStateSets: IStateSet[] = [
							...widget.statesets.slice(0, widgetStateSetIndex),
							newWidgetStateSet,
							...widget.statesets.slice(widgetStateSetIndex + 1)];

						const index = state.data.widgets.indexOf(widget);

						newData = tassign(state.data, {
							widgets: [
								...state.data.widgets.slice(0, index),
								tassign(widget, { statesets: newWidgetStateSets }),
								...state.data.widgets.slice(index + 1),
							]
						});
						newState = tassign(state, { data: newData, fromCommand: action.description });
					}
				}
			}
			break;

		case TimelineDataActions.CHANGE_WIDGETS_SAME_CONFIG:
			if (action.ids != null && action.config != null && action.stateSet != null && action.state != null) {

				const ids: number[] = action.ids;
				if (ids.length) {
					newState = state;
					for (let i = 0, l: number = ids.length; i < l; i++) {
						newState = timelineDataReducer(newState, {
							type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
							id: ids[i],
							config: action.config,
							stateSet: action.stateSet,
							state: action.state,
							description: action.description
						});
					}
				}
			}
			break;

		case TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG:
			if (action.widgets != null && action.stateSet != null && action.state != null) {

				const widgets: any[] = action.widgets;

				if (widgets.length) {
					newState = state;
					for (let i = 0, l: number = widgets.length; i < l; i++) {
						newState = timelineDataReducer(newState, {
							type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
							id: widgets[i].id,
							config: widgets[i].config,
							stateSet: action.stateSet,
							state: action.state,
							description: action.description
						});
					}
				}
			}
			break;

		case TimelineDataActions.REMOVE_WIDGET:
			if (action.id != null) {
				// find widget in order array
				findAndRemoveTrackByWidgetRecursive(state.data.order, action.id, newOrder);

				newData = tassign(state.data, {
					widgets: state.data.widgets.filter(w => w.id !== action.id),
					order: newOrder
				});

				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.REMOVE_WIDGETS:
			if (action.ids != null) {
				const ids: number[] = action.ids;
				if (ids.length) {
					newState = state;
					for (let i = 0, l: number = ids.length; i < l; i++) {
						newState = timelineDataReducer(newState, {
							type: TimelineDataActions.REMOVE_WIDGET,
							id: ids[i],
							description: action.description
						});
					}
				}
			}
			break;

		case TimelineDataActions.ADD_KEYFRAME:
			if (action.id != null && action.keyframe != null && action.legend != null) {
				widget = state.data.widgets.find(w => (w.id === action.id));
				widgetIndex = state.data.widgets.indexOf(widget);

				if (widget) {
					const animations: IAnimationData[] = widget.animations;
					if (animations) {
						const animation: IAnimationData = animations.find(anim => anim && anim.legend === action.legend);
						const animationIndex = animations.indexOf(animation);

						let newAnimation: IAnimationData;

						const newKeyframe = tassign(action.keyframe);

						if (animation == null) {
							// add new animation
							newAnimation = {
								legend: action.legend,
								relative: 'false',  // hardcoded for now
								data: [newKeyframe]
							};
						} else {
							const newKeyframeIndex = InteractivityWidgetAnimationService.searchCurrent(animation.data, action.keyframe.time) + 1; // NB!
							newAnimation = tassign(animation, {
								data: [
									...animation.data.slice(0, newKeyframeIndex),
									newKeyframe,
									...animation.data.slice(newKeyframeIndex)
								]
							});
						}

						const newAnimations: IAnimationData[] = [
							...widget.animations.slice(0, animationIndex),
							newAnimation,
							...widget.animations.slice(animationIndex + 1)
						];

						newData = tassign(state.data, {
							widgets: [
								...state.data.widgets.slice(0, widgetIndex),
								tassign(widget, { animations: newAnimations }),
								...state.data.widgets.slice(widgetIndex + 1),
							]
						});

						newState = tassign(state, { data: newData, fromCommand: action.description });
					}
				}
			}
			break;

		case TimelineDataActions.REMOVE_KEYFRAME:
			if (action.id && action.legend && action.keyframeIndex !== undefined) {
				widget = state.data.widgets.find(w => w.id === action.id);
				widgetIndex = state.data.widgets.indexOf(widget);

				if (widget) {
					const animations: IAnimationData[] = widget.animations;
					if (animations) {
						const animation: IAnimationData = animations.find(anim => anim && anim.legend === action.legend);
						const animationIndex = animations.indexOf(animation);
						const keyframeIndex = action.keyframeIndex;
						if (animation && animation.data && animation.data[keyframeIndex]) {
							const newAnimation: IAnimationData = { ...animation };
							newAnimation.data = [...animation.data];
							newAnimation.data.splice(keyframeIndex, 1);

							const newAnimations: IAnimationData[] = [
								...widget.animations.slice(0, animationIndex),
								newAnimation,
								...widget.animations.slice(animationIndex + 1)
							];

							newData = tassign(state.data, {
								widgets: [
									...state.data.widgets.slice(0, widgetIndex),
									tassign(widget, { animations: newAnimations }),
									...state.data.widgets.slice(widgetIndex + 1),
								]
							});
							newState = tassign(state, { data: newData, fromCommand: action.description });
						}
					}
				}
			}
			break;

		case TimelineDataActions.CHANGE_KEYFRAME:
			if (action.id != null && action.keyframe != null && action.legend != null && action.keyframeIndex != null) {
				widget = state.data.widgets.find(w => w.id === action.id);
				widgetIndex = state.data.widgets.indexOf(widget);

				if (widget) {
					const animations: IAnimationData[] = widget.animations;
					if (animations) {
						const animation: IAnimationData = animations.find(anim => anim && anim.legend === action.legend);
						const animationIndex = animations.indexOf(animation);
						const keyframeIndex = action.keyframeIndex;

						if (animation && animation.data && animation.data[keyframeIndex]) {
							let newAnimation: IAnimationData;

							const newKeyframe = tassign(action.keyframe);
							const newKeyframeIndex = InteractivityWidgetAnimationService.searchCurrent(animation.data, newKeyframe.time) + 1; // NB!
							if (newKeyframeIndex < keyframeIndex) {
								newAnimation = tassign(animation, {
									data: [
										...animation.data.slice(0, newKeyframeIndex),
										newKeyframe,
										...animation.data.slice(newKeyframeIndex, keyframeIndex),
										...animation.data.slice(keyframeIndex + 1),
									]
								});
							} else if (newKeyframeIndex > keyframeIndex) {
								newAnimation = tassign(animation, {
									data: [
										...animation.data.slice(0, keyframeIndex),
										...animation.data.slice(keyframeIndex + 1, newKeyframeIndex),
										newKeyframe,
										...animation.data.slice(newKeyframeIndex),
									]
								});
							} else { // newKeyframeIndex == keyframeIndex
								newAnimation = tassign(animation, {
									data: [
										...animation.data.slice(0, keyframeIndex),
										newKeyframe,
										...animation.data.slice(keyframeIndex + 1),
									]
								});
							}

							const newAnimations: IAnimationData[] = [
								...widget.animations.slice(0, animationIndex),
								newAnimation,
								...widget.animations.slice(animationIndex + 1)
							];

							newData = tassign(state.data, {
								widgets: [
									...state.data.widgets.slice(0, widgetIndex),
									tassign(widget, { animations: newAnimations }),
									...state.data.widgets.slice(widgetIndex + 1),
								]
							});
							newState = tassign(state, { data: newData, fromCommand: action.description });
						}
					}
				}
			}
			break;

		// for version 2.x
		case TimelineDataActions.ADD_TRACKGROUP:
			if (action.trackGroup != null) {
				const currentTrackGroups = state.data.order.filter(item => item.type === 'trackgroup');
				const newTrackGroup: IDataTrackAreaItem = { ...action.trackGroup, label: 'Track group ' + (currentTrackGroups.length + 1) };

				if (action.intoTrackGroupId == null) {	// put in the root
					newData = Object.assign({}, state.data, { order: state.data.order.concat(Object.assign({}, newTrackGroup)) });
					newState = tassign(state, { data: newData });
				} else {							// put in trackgroup with id=intoTrackGroupId
					newOrder = new Array<IDataTrackAreaItem>();
					findTrackGroupAndAddObjectRecursive(state.data.order, action.intoTrackGroupId, newTrackGroup, newOrder, 0);
					newData = tassign(state.data, { order: newOrder });
					newState = tassign(state, { data: newData });
				}

				// move selected tracks to group using reducer
				newState = timelineDataReducer(newState, {
					type: TimelineDataActions.ADD_TRACK_TO_TRACKGROUP,
					trackGroupId: newTrackGroup.id,
					tracks: action.tracks
				});

				newState = tassign(newState, { fromCommand: action.description });
			}
			break;

		case TimelineDataActions.ADD_TRACK_TO_TRACKGROUP:
			if (action.tracks != null && state.data.order.length > 0) {
				const group = state.data.order.find(item => item.id === action.trackGroupId);
				for (let i = 0, l: number = action.tracks.length; i < l; i++) {
					const dataTrack: IDataTrackAreaItem = state.data.order.find((item: IDataTrackAreaItem) => item.id === action.tracks[i].id);
					if (dataTrack) {
						group.tracks.push(dataTrack);
					}
				}
				const dataTrackIds = group.tracks.map(dataTrack => dataTrack.id);
				newOrder = state.data.order.filter(t => (dataTrackIds.indexOf(t.id) === -1));

				const newWidgets: IDataWidget[] = orderWidgetsByTracks(state.data.widgets, newOrder);

				newData = tassign(state.data, {
					widgets: newWidgets,
					order: newOrder
				});
				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});

			}
			break;

		case TimelineDataActions.REMOVE_TRACKGROUP:
			if (action.id != null) {
				findAndRemoveTrackGroupRecursive(state.data.order, action.id, newOrder);

				newData = tassign(state.data, {
					order: newOrder
				});

				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.UNGROUP_TRACKGROUP:
			if (action.id != null) {
				const trackGroupToUngroup = state.data.order.find(t => (t.type === 'trackgroup' && t.id === action.id));
				if (trackGroupToUngroup && trackGroupToUngroup.tracks && trackGroupToUngroup.tracks.length) {
					const groupIndex = state.data.order.indexOf(trackGroupToUngroup);
					let tmpOrder = [...state.data.order];
					tmpOrder.splice(groupIndex, 1);
					const p1 = tmpOrder.slice(0, groupIndex);
					const p2 = tmpOrder.slice(groupIndex, tmpOrder.length);
					tmpOrder = p1.concat(trackGroupToUngroup.tracks).concat(p2);
					newOrder = tmpOrder;
				}

				newData = tassign(state.data, {
					order: newOrder
				});

				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.CHANGE_TRACKGROUP:
			const trackGroup = state.data.order.find(track => track.id === action.id);
			const trackGroupIndex = state.data.order.indexOf(trackGroup);

			const newGroup = tassign(trackGroup);
			if (action.label !== undefined) {
				newGroup.label = action.label;
			}

			newData = tassign(state.data, {
				order: [
					...state.data.order.slice(0, trackGroupIndex),
					newGroup,
					...state.data.order.slice(trackGroupIndex + 1),
				]
			});
			newState = tassign(state, { data: newData, fromCommand: action.description });
			break;

		case TimelineDataActions.ADD_TRACK:		// used in reducer only (when adding a widget), do not call directly
			if (action.track != null) {
				const newTrack: IDataTrackAreaItem = tassign(action.track);

				if (!action.intoTrackGroupId) {	// put in the root
					if (action.track.targetIndex || action.track.targetIndex === 0) {
						newData = tassign(state.data, {
							order: [
								...state.data.order.slice(0, action.track.targetIndex),
								newTrack,
								...state.data.order.slice(action.track.targetIndex),
							]
						});
					} else {
						newData = Object.assign({}, state.data, { order: state.data.order.concat(Object.assign({}, newTrack)) });
					}
					newState = tassign(state, { data: newData, fromCommand: action.description });
				} else {							// put in trackgroup with id==intoTrackGroupId
					newOrder = new Array<IDataTrackAreaItem>();
					findTrackGroupAndAddObjectRecursive(state.data.order, action.intoTrackGroupId, newTrack, newOrder, action.track.targetIndex);
					newData = tassign(state.data, { order: newOrder });
					newState = tassign(state, {
						data: newData,
						fromCommand: action.description
					});
				}
			}
			break;

		case TimelineDataActions.CHANGE_IN_OUT_POINT:
			if (action.trackId != null && action.inOutPointId != null && action.inPoint != null && action.outPoint != null) {
				findAndChangeInOutPointRecursive(state.data.order, action.trackId, action.inOutPointId, action.inPoint, action.outPoint, newOrder);

				newData = tassign(state.data, {
					order: newOrder
				});

				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;

		case TimelineDataActions.MOVE_SELECTED_TRACKS_TO_INDEX:
			if (action.tracks != null && action.index != null &&
				state.data.order.length > 0 && action.index < state.data.order.length) {

				const selectedTracks = [];
				for (let i = 0, l: number = action.tracks.length; i < l; i++) {
					const track = state.data.order.find(t => (t.type === 'track' && t.id === action.tracks[i].id));
					selectedTracks.push(track);
				}

				if (selectedTracks != null) {
					const trackIndex = state.data.order.indexOf(selectedTracks[0]);

					if (action.index !== trackIndex) { // same spot
						const filtered = state.data.order.filter(t => (
							(t.type === 'track' && !action.tracks.some((track: ITrackAreaItem) => track.id === t.id)) || t.type === 'trackgroup'));

						if (action.index === 0) {
							for (let i = 0, l: number = selectedTracks.length; i < l; i++) {
								newOrder.push(selectedTracks[i]);  // at the start
							}
						}

						for (let i = 0, l: number = filtered.length; i < l; i++) {
							if (action.index !== 0 && action.index !== l && i === action.index) {
								selectedTracks.map(track => newOrder.push(track));  // in the middle
							}

							newOrder.push(filtered[i]);
						}
						if (action.index !== 0 && action.index >= filtered.length) {
							selectedTracks.map(track => newOrder.push(track)); // at the end
						}
						const newWidgets: IDataWidget[] = orderWidgetsByTracks(state.data.widgets, newOrder);

						newData = tassign(state.data, {
							widgets: newWidgets,
							order: newOrder
						});
						newState = tassign(state, {
							data: newData,
							fromCommand: action.description
						});
					}
				}
			}
			break;

		case TimelineDataActions.MOVE_TRACK_TO_INDEX:
			if (action.trackId != null && action.index != null &&
				state.data.order.length > 0 && action.index < state.data.order.length) {
				const track = state.data.order.find(t => (t.type === 'track' && t.id === action.trackId));

				if (track != null) {
					const trackIndex = state.data.order.indexOf(track);

					if (action.index !== trackIndex) { // same spot
						const filtered = state.data.order.filter(t => (
							(t.type === 'track' && t.id !== action.trackId) || t.type === 'trackgroup'));

						if (action.index === 0) {
							newOrder.push(track);  // at the start
						}

						for (let i = 0, l: number = filtered.length; i < l; i++) {
							if (action.index !== 0 && action.index !== l && i === action.index) {
								newOrder.push(track);	// in the middle
							}

							newOrder.push(filtered[i]);

							if (action.index !== 0 && action.index === l && i + 1 === action.index) {
								newOrder.push(track);		// at the end
							}
						}

						const newWidgets: IDataWidget[] = orderWidgetsByTracks(state.data.widgets, newOrder);

						newData = tassign(state.data, {
							widgets: newWidgets,
							order: newOrder
						});
						newState = tassign(state, {
							data: newData,
							fromCommand: action.description
						});
					}
				}
			}
			break;

		case TimelineDataActions.UPDATE_TRACK_ORDER:
			if (action.trackOrder && typeof action.trackOrder.length !== 'undefined') {
				newOrder = orderTracksByNewOrder(action.trackOrder, state.data.order);
				const newWidgets: IDataWidget[] = orderWidgetsByTracks(state.data.widgets, newOrder);
				newData = tassign(state.data, {
					widgets: newWidgets,
					order: newOrder
				});
				newState = tassign(state, {
					data: newData,
					fromCommand: action.description
				});
			}
			break;
	}
	if (newState && action.type) {
		newState.type = action.type;
	}

	const ret = newState || state;
	return ret;
}

// **************************************************************************************************************
// **********                    helper functions that are used in reducer                 **********************

function removeFaultyWidgetsAndTracks(state: ITimelineDataState) {
	if (!state.data) {
		return state;
	}
	const ret = state;
	const widgets = ret.data.widgets || [];
	const tracks = ret.data.order || [];
	const widgetRef = {};
	for (let i = widgets.length - 1; i >= 0; i--) {
		// fromAppId is set when the widget is part of an app
		if (!widgets[i] || widgets[i].fromAppId) {
			widgets.splice(i, 1);
		} else {
			widgetRef[widgets[i].id] = widgets[i];
		}
	}
	for (let z = tracks.length - 1; z >= 0; z--) {
		const track = tracks[z];
		if (!track || track.fromAppId || (track.widgetid && !widgetRef[track.widgetid])) {
			tracks.splice(z, 1);
		} else {
			if (track && track.tracks) {
				for (let j = track.tracks.length - 1; j >= 0; j--) {
					const trackInGroup = track.tracks[j];
					if (!trackInGroup || trackInGroup.fromAppId || (trackInGroup.widgetid && !widgetRef[trackInGroup.widgetid])) {
						track.tracks.splice(j, 1);
					}
				}
			}
		}
	}
	return ret;
}

function findAndChangeInOutPointRecursive(order: any[], trackId: string, iOId: string,
	inPoint: string, outPoint: string, newOrder: any[]): void {
	const track = order.find(t => (t.type === 'track' && t.id === trackId));

	if (track != null) {
		// we found the track in the root of the order Array, handling i-o-point now

		const ioPoints: IInOutPoint[] = track.inoutpoints;
		if (ioPoints != null) {
			let newIOPoint: IInOutPoint;

			const ioPoint = ioPoints.find(io => (io.id === iOId));

			// check for and fix incorrect times
			const ip = parseFloat(inPoint);
			const op = parseFloat(outPoint);
			if (op < ip) {
				// console.warn('inPoint', inPoint, 'outPoint', outPoint, 'ioPoint', ioPoint, 'fixing ', ioPoint.outpoint === outPoint ? 'in' : 'out');
				if (ioPoint && ioPoint.outpoint === outPoint) {
					// fix by changing inpoint
					inPoint = String(op - 0.08);
				} else {
					// fix by changing outpoint (default)
					outPoint = String(ip + 0.08);
				}
			}

			if (ioPoint == null) {
				// add new io point
				newIOPoint = { id: String(Math.floor(Math.random() * 9898946546) + 1), inpoint: inPoint, outpoint: outPoint };
			} else {
				newIOPoint = Object.assign({}, ioPoint, { inpoint: inPoint, outpoint: outPoint });
			}

			const newIOPoints: IInOutPoint[] = new Array<IInOutPoint>();
			const filteredIOPoints = ioPoints.filter(io => ((io.id !== iOId)));
			if (filteredIOPoints.length === 0) {
				newIOPoints.push(newIOPoint);
			} else {
				const ioPointIndex: number = ioPoints.indexOf(ioPoint);
				for (let i = 0, l: number = filteredIOPoints.length; i < l; i++) {
					if (ioPointIndex === 0 && i === 0) {	// keep order
						newIOPoints.push(newIOPoint);
					}

					newIOPoints.push(filteredIOPoints[i]);	// keep order

					if (ioPointIndex !== 0 && i === ioPointIndex - 1) {	// keep order
						newIOPoints.push(newIOPoint);
					}
				}
			}

			const newTrack: any = Object.assign({}, track, { inoutpoints: newIOPoints });
			const filtered = order.filter(t => ((t.type === 'track' && t.id !== trackId) || t.type === 'trackgroup'));
			if (filtered.length === 0) {
				newOrder.push(newTrack);
			} else {
				const trackIndex = order.indexOf(track);
				for (let j = 0, l: number = filtered.length; j < l; j++) {
					if (trackIndex === 0 && j === 0) { // keep order
						newOrder.push(newTrack);
					}

					newOrder.push(filtered[j]); // keep order

					if (trackIndex !== 0 && j === trackIndex - 1) { // keep order
						newOrder.push(newTrack);
					}
				}
			}
		}
	} else {
		for (let j = 0, l: number = order.length; j < l; j++) {
			newOrder.push(order[j]);
		}
		for (let i = 0, l: number = order.length; i < l; i++) {
			if (order[i].type === 'trackgroup') {
				newOrder[i] = { ...order[i] }; // this is why it works, because the object is NEW
				newOrder[i].tracks = [];

				findAndChangeInOutPointRecursive(order[i].tracks, trackId, iOId, inPoint, outPoint, newOrder[i].tracks);
			}
		}
	}
}

function findAndRemoveTrackByWidgetRecursive(order: any[], widgetId: string, newOrder: any[]): void {
	const track: any = order.find(t => (t.type === 'track' && t.widgetid === widgetId));

	if (track != null) {
		// we found the widget in a track in the root of the order Array, removing track now
		const filtered = order.filter(t => ((t.type === 'track' && t.widgetid !== widgetId) || t.type === 'trackgroup'));
		for (let j = 0, l: number = filtered.length; j < l; j++) {
			newOrder.push(filtered[j]);
		}
	} else {
		for (let j = 0, l: number = order.length; j < l; j++) {
			newOrder.push(order[j]);
		}
		for (let i = 0, l: number = order.length; i < l; i++) {
			if (order[i].type === 'trackgroup') {
				newOrder[i] = { ...order[i] }; // this is why it works, because the object is NEW
				newOrder[i].tracks = [];
				findAndRemoveTrackByWidgetRecursive(order[i].tracks, widgetId, newOrder[i].tracks);
			}
		}
	}
}

function findAndRemoveTrackGroupRecursive(order: any[], trackGroupId: string, newOrder: any[]): void {
	const trackGroup: any = order.find(t => (t.type === 'trackgroup' && t.id === trackGroupId));

	if (trackGroup != null) {
		// we found the trackgroup in the root of the order Array, removing track now
		const filtered = order.filter(t => ((t.type === 'trackgroup' && t.id !== trackGroupId) || t.type === 'track'));
		for (let j = 0, l: number = filtered.length; j < l; j++) {
			newOrder.push(filtered[j]);
		}
	} else {
		for (let j = 0, l: number = order.length; j < l; j++) {
			newOrder.push(order[j]);
		}

		for (let i = 0, l: number = order.length; i < l; i++) {
			if (order[i].type === 'trackgroup') {
				newOrder[i] = { ...order[i] }; // this is why it works, because the object is NEW
				newOrder[i].tracks = [];
				findAndRemoveTrackGroupRecursive(order[i].tracks, trackGroupId, newOrder[i].tracks);
			}
		}
	}
}

function findTrackGroupAndAddObjectRecursive(order: any[], trackGroupId: string, obj: any,
	newOrder: any[], targetIndex: number): void {
	const trackGroup = order.find(t => (t.type === 'trackgroup' && t.id === trackGroupId));

	if (trackGroup != null) {
		const newObjs = tassign(obj);

		const newTracks: any = Object.assign({}, {
			tracks: [
				...trackGroup.tracks.slice(0, targetIndex),
				newObjs,
				...trackGroup.tracks.slice(targetIndex),
			]
		});
		const newTrackGroup: any = Object.assign({}, trackGroup, newTracks);

		const filtered = order.filter(t => (t.type === 'track' || (t.type === 'trackgroup' && t.id !== trackGroupId)));

		if (filtered.length === 0) {		//  nothing in tracks beside current trackgroup
			newOrder.push(newTrackGroup);
		} else {
			const trackGroupIndex = order.indexOf(trackGroup);
			for (let j = 0, l: number = filtered.length; j < l; j++) {
				if (trackGroupIndex === 0 && j === 0) { // keep order
					newOrder.push(newTrackGroup);
				}

				newOrder.push(filtered[j]); // keep order

				if (trackGroupIndex !== 0 && j === trackGroupIndex - 1) { // keep order
					newOrder.push(newTrackGroup);
				}
			}
		}
	} else {
		for (let j = 0, l: number = order.length; j < l; j++) {
			newOrder.push(order[j]);
		}

		for (let i = 0, l: number = order.length; i < l; i++) {
			if (order[i].type === 'trackgroup') {
				newOrder[i] = { ...order[i] }; // this is why it works, because the object is NEW
				newOrder[i].tracks = [];
				findTrackGroupAndAddObjectRecursive(order[i].tracks, trackGroupId, obj, newOrder[i].tracks, 0);
			}
		}
	}
}

function orderTracksByNewOrder(newTrackOrder: ITrackAreaItem[], oldDataOrder: IDataTrackAreaItem[]) {
	const trackRef: any = {};
	oldDataOrder.forEach((track: IDataTrackAreaItem) => {
		trackRef[track.id] = track;
		if (track && track.tracks) {
			track.tracks.forEach((track2: IDataTrackAreaItem) => {
				trackRef[track2.id] = track2;
			});
		}
	});
	const newOrder: IDataTrackAreaItem[] = [];

	newTrackOrder.forEach((t: any) => {
		if (t.id && trackRef[t.id]) {
			const trackCopy = { ...trackRef[t.id] };
			if (t && t.type === 'TrackGroup' && t.tracks) {
				trackCopy.tracks = [];
				t.tracks.forEach((t2: any) => {
					trackCopy.tracks.push(trackRef[t2.id]);
				});
			}

			newOrder.push(trackCopy);
		}
	});
	// console.warn('newOrder', JSON.parse(JSON.stringify(newOrder)));
	return newOrder;
}

function orderWidgetsByTracks(widgets: IDataWidget[], order: IDataTrackAreaItem[]): IDataWidget[] {
	// sort widgets
	const newWidgets: IDataWidget[] = [];

	for (let i = 0, l: number = order.length; i < l; i++) {
		if (order[i].type === 'track') {
			const foundWidget = widgets.find(widget => widget && widget.id === order[i].widgetid);
			newWidgets.push(foundWidget);
		} else if (order[i].type === 'trackgroup') {
			for (let j = 0, k: number = order[i].tracks.length; j < k; j++) {
				const foundWidgetInGroup = widgets.find(widget => widget && widget.id === order[i].tracks[j].widgetid);
				newWidgets.push(foundWidgetInGroup);
			}
		}
	}
	return newWidgets;
}

function findTrackByWidgetId(widgetId: string, order: IDataTrackAreaItem[]): { track: IDataTrackAreaItem; trackIndex: number; groupId?: string } {
	const ret: any = {};

	for (let i = 0, l: number = order.length; i < l; i++) {
		if (order[i].type === 'track') {
			const t = order[i];
			if (t && t.widgetid && t.widgetid === widgetId) {
				ret.track = t;
				ret.trackIndex = i;
			}
		} else if (order[i].type === 'trackgroup') {
			for (let j = 0, k: number = order[i].tracks.length; j < k; j++) {
				const t2 = order[i].tracks[j];
				if (t2 && t2.widgetid && t2.widgetid === widgetId) {
					ret.track = t2;
					ret.trackIndex = j;
					ret.groupId = order[i].id;
				}
			}
		}
	}
	return ret;
}
